<script setup lang="ts">
    import { ref, watch } from 'vue';
    import InsightsWidget from './InsightsWidget.vue';
    import InsightsIcon from './InsightsIcon.vue';
    import type { InsightsIconName } from '../insights-icons';
    import type { InsightsIssuesReportData } from '../insights-dummy-data';
    import { getInsightsAiSummary } from '../insights-ai';
    import type { InsightsAiInsightKind, InsightsAiSummary } from '../insights-ai';

    const props = defineProps<{
        report: InsightsIssuesReportData;
        monthLabel: string;
    }>();

    const KIND_ICONS: Record<InsightsAiInsightKind, InsightsIconName> = {
        positive: 'arrowUp',
        negative: 'arrowDown',
        recommendation: 'star',
    };

    const summary = ref<InsightsAiSummary | null>(null);
    const generating = ref(false);

    // Ignore responses for reports the user has already switched away from
    let requestId = 0;
    async function generate() {
        const current = ++requestId;
        generating.value = true;
        try {
            const result = await getInsightsAiSummary(props.report, props.monthLabel);
            if (current === requestId) summary.value = result;
        } finally {
            if (current === requestId) generating.value = false;
        }
    }

    // Generated on demand only; a new report invalidates the previous summary
    watch(
        () => props.report,
        () => {
            requestId++;
            summary.value = null;
            generating.value = false;
        }
    );
</script>

<template>
    <InsightsWidget
        class="insights-ai"
        :class="{ 'insights-ai-generating': generating }"
        title="AI insights"
        subtitle="Generated from this report"
        icon="wandStars">
        <template v-if="!summary" #actions>
            <button type="button" class="btn btn-default btn-flex btn-sm hidden-print" :disabled="generating" @click="generate">
                <InsightsIcon icon="wandStars" :size="16" />
                Generate
            </button>
        </template>

        <template v-if="summary">
            <p class="m-lg-b">{{ summary.Summary }}</p>

            <ul class="insights-ai-list list-unstyled m-0-all">
                <li v-for="insight in summary.Insights" :key="insight.Title" class="insights-ai-item d-flex gap-sm" :class="`is-${insight.Kind}`">
                    <span class="insights-ai-item-icon d-inline-flex align-items-center justify-content-center flex-shrink-0">
                        <InsightsIcon :icon="KIND_ICONS[insight.Kind]" :size="16" />
                    </span>
                    <div>
                        <div class="fw-600">{{ insight.Title }}</div>
                        <div class="text-sm">{{ insight.Text }}</div>
                    </div>
                </li>
            </ul>

            <p class="text-sm m-sm-t m-0-b">AI-generated content may be inaccurate. Verify important figures in the report.</p>
        </template>

        <p v-else class="text-sm m-0-b">Generate an AI summary of this report's key changes and recommendations.</p>
    </InsightsWidget>
</template>

